import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { createRequest, getRequestEmployees } from 'features/requests/requestsSlice'
import { getShifts } from 'features/shifts/shiftsSlice'
import { ICreateRequestPayload } from 'types/requests.interface'
import { ISelectValue } from 'constants/globalTypes'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { Modal } from 'components/Modal/Modal'
import { Input } from 'components/Input/Input'
import { Button } from 'components/ui/Button/Button'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import useModalClose from 'hooks/useModalClose'
import { CursorPointer, Flex } from 'styling/GlobalStyles'
import { TitleText } from 'styling/Typography'
import {
  requestTypeOptions,
  userName,
  RANGE_TYPES,
  REQ_ANNUAL,
  REQ_SHIFT,
  REQ_OVERTIME,
} from '../requestsData'
import { FormStack, Row } from './NewRequestModal.styled'
import { fetchAuthSession } from 'aws-amplify/auth'

export const NewRequestModal = ({
  closeModal,
}: {
  closeModal: (submitted?: boolean, forSelf?: boolean) => void
}) => {
  const dispatch = useDispatch<AppDispatch>()
  useModalClose(() => closeModal())
  const { employees } = useSelector((state: RootState) => state.requests)
  const { shifts } = useSelector((state: RootState) => state.shifts)

  useEffect(() => {
    dispatch(getRequestEmployees({ page: 1, limit: 99 }))
    dispatch(getShifts({ page: 1, limit: 99 }))
  }, [dispatch])

  const [forId, setForId] = useState<number>(0)
  const [type, setType] = useState<number>(REQ_ANNUAL)
  const [from, setFrom] = useState<Date | null>(null)
  const [to, setTo] = useState<Date | null>(null)
  const [reason, setReason] = useState('')
  const [coverageName, setCoverageName] = useState('')
  const [requestedShiftId, setRequestedShiftId] = useState<number | null>(null)
  const [hours, setHours] = useState('')
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false)

  useEffect(() => {
    let mounted = true
    fetchAuthSession()
      .then((session) => {
        const groups = session.tokens?.idToken?.payload['cognito:groups'] as string[] | undefined
        if (mounted) setIsCompanyAdmin(Boolean(groups?.includes('company_administrator')))
      })
      .catch(() => {
        if (mounted) setIsCompanyAdmin(false)
      })
    return () => {
      mounted = false
    }
  }, [])

  const isRange = RANGE_TYPES.includes(type)

  const forOptions = [
    ...(!isCompanyAdmin ? [{ value: 0, label: 'Myself' }] : []),
    ...(employees?.data || []).map((e) => ({
      value: e.id,
      label: e.jobTitle ? `${userName(e)} · ${e.jobTitle}` : userName(e),
    })),
  ]

  const coverageOptions = (employees?.data || []).map((e) => ({ value: e.id, label: userName(e) }))
  const shiftOptions = (shifts || []).map((s: any) => ({ value: s.id, label: s.name || `Shift #${s.id}` }))

  const disabled =
    !from ||
    (isRange && !to) ||
    (type === REQ_SHIFT && !requestedShiftId) ||
    (type === REQ_OVERTIME && !hours)

  const handleSave = async () => {
    if (disabled || !from) return
    const toDate = isRange ? to || from : from
    const forSelf = forId === 0
    const payload: ICreateRequestPayload = {
      requesterId: forSelf ? undefined : forId,
      type,
      from: from.toISOString(),
      to: toDate.toISOString(),
      reason: reason.trim() || undefined,
      coverageName: type === REQ_SHIFT ? coverageName || undefined : undefined,
      requestedShiftId: type === REQ_SHIFT ? requestedShiftId ?? undefined : undefined,
      overtimeHours: type === REQ_OVERTIME ? Number(hours) : undefined,
    }
    const resp = await dispatch(createRequest(payload))
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((resp as any).meta?.requestStatus === 'fulfilled') closeModal(true, forSelf)
  }

  return (
    <Modal black>
      <Flex column width='100%' gap='1rem'>
        <Flex between width='100%' center style={{ marginBottom: '0.5rem' }}>
          <TitleText weight='bold'>New Request</TitleText>
          <CursorPointer onClick={() => closeModal()}>
            <CloseIcon />
          </CursorPointer>
        </Flex>

        <FormStack>
          <Row>
            <HonestSelectInput
              nomarg
              labelText='For'
              options={forOptions}
              defaultValue={forId}
              onChange={(v: ISelectValue) => setForId(Number(v.value))}
            />
            <HonestSelectInput
              nomarg
              labelText='Type'
              options={requestTypeOptions}
              defaultValue={type}
              onChange={(v: ISelectValue) => setType(Number(v.value))}
            />
          </Row>

          {isRange ? (
            <Row>
              <CalendarInput nomarg labelText='From' minDate={new Date()} defaultValue={from} onChange={(d: Date | null) => setFrom(d)} />
              <CalendarInput nomarg labelText='To' minDate={from || new Date()} defaultValue={to} onChange={(d: Date | null) => setTo(d)} />
            </Row>
          ) : (
            <Row>
              <CalendarInput nomarg labelText='Date' minDate={new Date()} defaultValue={from} onChange={(d: Date | null) => setFrom(d)} />
              {type === REQ_OVERTIME && (
                <Input nomarg type='number' labelText='Extra hours' placeholder='e.g. 4' value={hours} onChange={(e) => setHours(e.target.value)} />
              )}
            </Row>
          )}

          {type === REQ_SHIFT && (
            <Row>
              <HonestSelectInput
                nomarg
                labelText='Requested shift'
                options={shiftOptions}
                onChange={(v: ISelectValue) => setRequestedShiftId(v ? Number(v.value) : null)}
              />
              <HonestSelectInput
                nomarg
                labelText='Coverage person'
                options={coverageOptions}
                onChange={(v: ISelectValue) => setCoverageName(v ? String(v.label) : '')}
              />
            </Row>
          )}

          <Input nomarg labelText='Reason' placeholder='Reason for this request…' value={reason} onChange={(e) => setReason(e.target.value)} />
        </FormStack>

        <Button style={{ marginTop: '0.5rem', width: '100%' }} variant='primary' disabled={disabled} onClick={handleSave}>
          Submit request
        </Button>
      </Flex>
    </Modal>
  )
}
